import type { FunctionComponent } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import Link from '@atlaskit/link';
import { xcss, Box } from '@atlaskit/primitives';
import {
  ATLASSIAN_PRIVACY_POLICY_URL,
  ATLASSIAN_TERMS_OF_USE_URL,
} from '../urls';

interface LegalDisclaimerMessages {
  agreementHeader: MessageDescriptor;
  /** The message containing {privacyPolicyLink} and {termsOfUseLink} placeholders. */
  agreementLinks: MessageDescriptor;
  privacyPolicy: MessageDescriptor;
  termsOfUse: MessageDescriptor;
}

export interface ForgeApp {
  name: string;
  vendorName?: string;
  privacyPolicy?: string;
}

export interface LegalDisclaimerProps {
  messages: LegalDisclaimerMessages;
  /** Optional product name interpolated into {brand} in agreementHeader. */
  product?: string;
  forgeApp?: ForgeApp;
}

const disclaimerStyle = xcss({
  color: 'color.text.subtle',
  font: 'font.body.small',
});

const compactParagraphStyle = xcss({
  margin: 'space.0',
});

const LegalDisclaimer: FunctionComponent<LegalDisclaimerProps> = ({
  messages,
  product,
  forgeApp,
}) => {
  const privacyPolicy = <FormattedMessage {...messages.privacyPolicy} />;
  const termsOfUse = <FormattedMessage {...messages.termsOfUse} />;

  const privacyPolicyLink = (
    <Link href={ATLASSIAN_PRIVACY_POLICY_URL}>{privacyPolicy}</Link>
  );

  const termsOfUseLink = (
    <Link href={ATLASSIAN_TERMS_OF_USE_URL}>{termsOfUse}</Link>
  );

  const forgeAppValues = forgeApp && {
    appName: forgeApp.name,
    hasVendor: Boolean(forgeApp.vendorName),
    vendorName: forgeApp.vendorName,
    appPrivacyPolicyLink: forgeApp.privacyPolicy ? (
      <Link href={forgeApp.privacyPolicy}>{privacyPolicy}</Link>
    ) : (
      privacyPolicy
    ),
  };

  return (
    <Box xcss={disclaimerStyle}>
      <Box as="p" xcss={compactParagraphStyle}>
        <FormattedMessage {...messages.agreementHeader} values={{ product }} />{' '}
        <FormattedMessage
          {...messages.agreementLinks}
          values={{ privacyPolicyLink, termsOfUseLink, ...forgeAppValues }}
        />
      </Box>
    </Box>
  );
};

export default LegalDisclaimer;
